import { useMemo, type PropsWithChildren } from 'react';

import type {
  MaxScaleBoundary,
  MinScaleBoundary,
} from '../../../../charts/core/types/min-max-config.js';
import type { MinMax } from '../../../../charts/core/types/min-max.js';
import {
  resolveMinMax,
  resolveTimeMinMax,
} from '../../../../charts/core/utils/get-min-max-for-scale.js';
import type { GanttSegmentData } from '../../../../charts/gantt-chart-v2/types/gantt-chart.js';
import {
  isAccessorFnColumnDef,
  isAccessorKeyColumnDef,
  isDisplayColumnDef,
} from '../../hooks/useTable/datatablev2-typeguards.js';
import type { ColumnDef, TableOptions } from '../../hooks/useTable/types.js';
import { isDatatableV2GanttColumnConfig } from '../ColumnTypes/ColumnTypes.js';

// Extend the tanstack table's state for the Gantt chart.
export interface DataTableV2GanttChartState {
  /**
   * Record of min/max values for gantt columns.
   */
  ganttMinMaxValues?: Record<string, MinMax>;
}

// Typeguard for gantt chart data
function isDataTableV2GanttData(obj: unknown): obj is GanttSegmentData {
  return typeof obj === 'object' && obj !== null && 'start' in obj;
}

/**
 * Retreives data for a specific column from a row entry.
 *
 * @param row - data row
 * @param columnDef - composed column def (useColumns)
 * @returns column data
 * @internal
 */
export function getDataFromRow<TData, TValue>(
  row: TData,
  columnDef: ColumnDef<TData, TValue>,
) {
  if (isAccessorKeyColumnDef(columnDef)) {
    return (row as Record<string, unknown>)[columnDef.accessorKey];
  }
  if (isAccessorFnColumnDef(columnDef)) {
    return columnDef.accessorFn(row, 0);
  }
}

/**
 * Gets the overall min and max values in a gantt column.
 *
 * @param data - table data
 * @param columns - column definition from table options
 * @param getSubRows - function used to access subRows for a given row
 * @returns record of min/max value per column
 * @internal
 */
export function getMinMaxFromGanttData<TData, TValue>(
  data: TData[],
  columns: ColumnDef<TData, TValue>[],
  getSubRows:
    | ((originalRow: TData, index: number) => TData[] | undefined)
    | undefined,
): Record<string, MinMax> {
  const minMaxArray = columns
    .filter(
      (columnDef) =>
        isDisplayColumnDef(columnDef) && columnDef.columnType === 'gantt',
    )
    .map((columnDef) => {
      const config = columnDef.meta?.config;

      let configMin: MinScaleBoundary = 'data-min';
      let configMax: MaxScaleBoundary = 'data-max';
      let xAxisType: 'numerical' | 'time' | undefined = 'numerical';

      if (isDatatableV2GanttColumnConfig(config)) {
        configMin = config.min ?? configMin;
        configMax = config.max ?? configMax;
        xAxisType = config.xAxisType ?? xAxisType;
      }

      let min = Number.POSITIVE_INFINITY;
      let max = Number.NEGATIVE_INFINITY;

      /**
       * Check gantt start/end values in a row to find the overall min/max values.
       * @param row - data row
       */
      const findMinMax = (row: TData) => {
        const ganttData = getDataFromRow(row, columnDef);

        const ganttSegments = Array.isArray(ganttData)
          ? ganttData
          : [ganttData];

        ganttSegments.forEach((segment) => {
          if (isDataTableV2GanttData(segment)) {
            const { start, end } = segment;
            min = Math.min(min, start);
            max = Math.max(max, end ?? start);
          }
        });

        const subRows = getSubRows?.(row, 0);

        // recursively traverse sub-rows
        subRows?.forEach((subRow) => findMinMax(subRow));
      };

      for (const row of data) {
        findMinMax(row);
      }

      return [
        columnDef.id,
        xAxisType === 'numerical'
          ? resolveMinMax({ min, max }, { min: configMin, max: configMax })
          : resolveTimeMinMax({ min, max }, { min: configMin, max: configMax }),
      ];
    });

  return Object.fromEntries(minMaxArray);
}

/**
 * Configuration hook for the DataTableV2 Gantt Chart feature.
 * @internal
 */
export function useGanttChart<TData, TValue>(
  props: PropsWithChildren,
  options: TableOptions<TData>,
) {
  const ganttMinMaxValues = useMemo(
    () =>
      getMinMaxFromGanttData<TData, TValue>(
        options.data,
        options.columns,
        options.getSubRows,
      ),
    [options.columns, options.data, options.getSubRows],
  );

  options.state = { ...options.state, ganttMinMaxValues };
}
